import ical from "ical.js";
import { formatSubscribers, parseJcalDate, SUBSCRIBER_COUNT_THRESHOLD } from "src/utils/Utils.js";
import { eventImageUrl, tileSizedImage } from "src/utils/imageUrl.js";

/** tvmaze episodes with no air time get an 11:11:11 placeholder start. */
const PLACEHOLDER_DTSTART_RE = /DTSTART[^:\n]*:\d{8}T111111/;

/** How recently a past event can be and still be worth surfacing on the tile. */
const LAST_EVENT_WINDOW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Bounds the VEVENT parse cache (FIFO eviction). */
const PARSE_CACHE_MAX = 500;
const parseCache = new Map();

/**
 * Parses the raw VEVENT string the sections API attaches into an ical.js
 * VEVENT component (null when absent/unparseable). Memoized — several readers
 * (dates, summary, artwork, matchup) run per tile per render, and the hero's
 * countdown ticker re-reads every second.
 *
 * @param {string|null|undefined} mostRecentEvent
 * @returns {object|null}
 */
export function parseMostRecentEvent(mostRecentEvent) {
  if (!mostRecentEvent || typeof mostRecentEvent !== "string") return null;
  if (parseCache.has(mostRecentEvent)) return parseCache.get(mostRecentEvent);
  let event = null;
  try {
    const component = new ical.Component(
      ical.parse(`BEGIN:VCALENDAR\n${mostRecentEvent}\nEND:VCALENDAR`),
    );
    event = component.getFirstSubcomponent("vevent");
  } catch {
    event = null;
  }
  if (parseCache.size >= PARSE_CACHE_MAX) {
    parseCache.delete(parseCache.keys().next().value);
  }
  parseCache.set(mostRecentEvent, event);
  return event;
}

/**
 * Parses the start date out of the raw VEVENT the sections API attaches.
 *
 * @param {string|null|undefined} mostRecentEvent
 * @returns {Date|null}
 */
export function nextEventStart(mostRecentEvent) {
  const { dtstart } = eventJcalDates(mostRecentEvent);
  if (!dtstart) return null;
  const start = parseJcalDate(dtstart).eventParsed;
  return Number.isNaN(start.getTime()) ? null : start;
}

/**
 * Extracts the featured event's dtstart/dtend as jCal property arrays, the shape
 * `useUpcomingEventStatus` consumes to drive its live/countdown ticker. `dtend`
 * falls back to `dtstart` for that shape; `hasEnd` says whether a real DTEND
 * existed (a DTEND-less event's live window is the caller's to define).
 *
 * @param {string|null|undefined} mostRecentEvent
 * @returns {{ dtstart: Array|null, dtend: Array|null, hasEnd: boolean }}
 */
export function eventJcalDates(mostRecentEvent) {
  try {
    const event = parseMostRecentEvent(mostRecentEvent);
    if (!event) return { dtstart: null, dtend: null, hasEnd: false };
    const dtstart = event.getFirstProperty("dtstart")?.toJSON() ?? null;
    const realEnd = event.getFirstProperty("dtend")?.toJSON() ?? null;
    return { dtstart, dtend: realEnd ?? dtstart, hasEnd: Boolean(realEnd) };
  } catch {
    return { dtstart: null, dtend: null, hasEnd: false };
  }
}

/**
 * The featured event's SUMMARY text, or null when absent/unparseable.
 *
 * @param {string|null|undefined} mostRecentEvent
 * @returns {string|null}
 */
export function eventSummary(mostRecentEvent) {
  try {
    const summary = parseMostRecentEvent(mostRecentEvent)?.getFirstPropertyValue("summary");
    return typeof summary === "string" && summary.trim() ? summary.trim() : null;
  } catch {
    return null;
  }
}

/**
 * "<matchup> · <Thu 4:00 PM>" line for the featured event, leading emoji
 * markers stripped. When the calendar's own name is one side of an
 * "A at B" summary it is dropped: "at Guardians" (away) / "vs Diamondbacks"
 * (home). Null when the event has no summary or start.
 *
 * @param {string|null|undefined} mostRecentEvent
 * @param {string} [calendarName]
 * @returns {string|null}
 */
export function eventMatchupLabel(mostRecentEvent, calendarName = "") {
  const summary = eventSummary(mostRecentEvent)?.replace(/^[^\p{L}\p{N}]+/u, "");
  const start = nextEventStart(mostRecentEvent);
  if (!summary || !start) return null;

  let matchup = summary;
  const name = calendarName.trim().toLowerCase();
  const sep = [" at ", " @ "].find((candidate) => summary.includes(candidate));
  if (name && sep) {
    const away = summary.slice(0, summary.indexOf(sep)).trim();
    const home = summary.slice(summary.indexOf(sep) + sep.length).trim();
    if (away.toLowerCase() === name) matchup = `at ${home}`;
    else if (home.toLowerCase() === name) matchup = `vs ${away}`;
  }

  const dow = start.toLocaleDateString("en-US", { weekday: "short" });
  const time = start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${matchup} · ${dow} ${time}`;
}

/**
 * Extracts the image candidates from the featured raw VEVENT the sections API
 * attaches to each calendar. These feed the same fallback sequence as the
 * recommended-calendar cards when a calendar profile image is unavailable.
 *
 * @param {string|null|undefined} mostRecentEvent
 * @param {string|null|undefined} handle
 * @returns {{ homeTeamLogo: string|null, eventImageUrl: string|null, background: string|null }}
 */
export function eventArtworkSources(mostRecentEvent, handle) {
  const emptySources = {
    homeTeamLogo: null,
    eventImageUrl: null,
    background: null,
  };

  try {
    const event = parseMostRecentEvent(mostRecentEvent);
    if (!event) return emptySources;

    const eventId = event.getFirstPropertyValue("uid");

    return {
      homeTeamLogo: event.getFirstPropertyValue("x-homelogourl") ?? null,
      eventImageUrl: eventImageUrl(handle, eventId),
      background: tileSizedImage(event.getFirstPropertyValue("x-backgroundurl")) ?? null,
    };
  } catch {
    return emptySources;
  }
}

/**
 * Label for streaming drops whose start is the tvmaze placeholder time:
 * "Season premiere" for first episodes and announced seasons, "New episode"
 * otherwise. Null when the event has a real air time.
 *
 * @param {string|null|undefined} mostRecentEvent
 * @returns {string|null}
 */
export function streamingDropLabel(mostRecentEvent) {
  if (!mostRecentEvent || typeof mostRecentEvent !== "string") return null;
  if (!PLACEHOLDER_DTSTART_RE.test(mostRecentEvent)) return null;
  try {
    const event = parseMostRecentEvent(mostRecentEvent);
    const location = String(event?.getFirstPropertyValue("location") ?? "");
    const summary = String(event?.getFirstPropertyValue("summary") ?? "");
    if (
      /season premieres/i.test(location) ||
      /^S\d+\s*E1\b/i.test(location) ||
      /season \d+ is here/i.test(summary)
    ) {
      return "Season premiere";
    }
    return "New episode";
  } catch {
    return "New episode";
  }
}

/**
 * Formats the calendar's featured event as a short "MMM D · h:mm AM" label for
 * the tile overlay. Streaming drops with a placeholder air time show what the
 * date means ("MMM D · New episode") instead of the fake time. Calendars with
 * no parseable event produce no label.
 *
 * @param {string|null|undefined} mostRecentEvent
 * @returns {string|null}
 */
export function nextEventLabel(mostRecentEvent) {
  const start = nextEventStart(mostRecentEvent);
  if (!start) return null;
  const date = start.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const drop = streamingDropLabel(mostRecentEvent);
  if (drop) return `${date} · ${drop}`;
  const time = start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${date} · ${time}`;
}

/**
 * Short "Next: <weekday, date>" label for a calendar whose featured event is
 * upcoming — the leaderboard's single-line meta. Null when the featured event is
 * in the past or absent (the caller falls back to the subscriber count).
 *
 * @param {string|null|undefined} mostRecentEvent
 * @param {Date} [now]
 * @returns {string|null}
 */
export function nextEventShortLabel(mostRecentEvent, now = new Date()) {
  const start = nextEventStart(mostRecentEvent);
  if (!start || start.getTime() < now.getTime()) return null;
  const date = start.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  return `Next: ${date}`;
}

/**
 * "Last event · N ago" label for a calendar whose featured event is in the past.
 * Only recently-past events (within `LAST_EVENT_WINDOW_DAYS`) get a label — a
 * months-old date isn't useful, so it returns null and the tile shows no pill.
 *
 * @param {string|null|undefined} mostRecentEvent
 * @param {Date} [now]
 * @returns {string|null}
 */
export function pastEventLabel(mostRecentEvent, now = new Date()) {
  const start = nextEventStart(mostRecentEvent);
  if (!start) return null;
  const elapsed = now.getTime() - start.getTime();
  if (elapsed < 0) return null; // upcoming, not past
  const days = Math.floor(elapsed / DAY_MS);
  if (days > LAST_EVENT_WINDOW_DAYS) return null;
  let relative;
  if (days === 0) relative = "today";
  else if (days === 1) relative = "yesterday";
  else if (days < 7) relative = `${days} days ago`;
  else {
    const weeks = Math.floor(days / 7);
    relative = weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  }
  return `Last event · ${relative}`;
}

/**
 * The single line rendered below every tile's name — consistent across the
 * leaderboard, section rails, and A-Z rows. Priority:
 *   1. subscriber count, when high enough to signal popularity;
 *   2. else the next event ("Next: Sat, Sep 6"), when one is upcoming;
 *   3. else a status pill ("New on Stanza" / "Fan Favorite" / ...);
 *   4. else blank.
 * Pass the result to SubscriberStatusBadge.
 *
 * @param {{ mostRecentEvent?: string|null, subscribers?: number, createdAt?: string|null }} calendar
 * @returns {string}
 */
export function tileMetaLabel(calendar) {
  const subscribers = calendar.subscribers ?? 0;
  if (subscribers > SUBSCRIBER_COUNT_THRESHOLD) {
    return formatSubscribers(subscribers, { includeSubscriberText: true });
  }
  const nextEvent = nextEventShortLabel(calendar.mostRecentEvent);
  if (nextEvent) return nextEvent;
  return formatSubscribers(subscribers, {
    includeSubscriberText: true,
    showAlternativeText: true,
    createdAt: calendar.createdAt,
  });
}

/**
 * True when a calendar is TV/streaming content, detected by the "📺" marker
 * tvmaze-sourced descriptions carry. Used to render a whole section as posters
 * when it contains TV titles.
 *
 * @param {{ description?: string }} calendar
 * @returns {boolean}
 */
export function isTvCalendar(calendar) {
  return /📺/.test(calendar?.description || "");
}

/**
 * Round-robin interleave across ordered pools with cross-pool dedupe: one
 * calendar per pool per round (skipping ids already taken, without stalling
 * the pool) until `cap` items are out or every pool is exhausted.
 *
 * @param {Array<Array<object>>} pools - Each pool already ordered best-first.
 * @param {number} cap
 * @returns {Array<object>}
 */
export function roundRobinPools(pools, cap) {
  const keyOf = (calendar) => calendar.calendarId ?? calendar.handle;
  const cursors = pools.map(() => 0);
  const seen = new Set();
  const out = [];
  let exhausted = false;
  while (out.length < cap && !exhausted) {
    exhausted = true;
    for (let p = 0; p < pools.length && out.length < cap; p += 1) {
      const pool = pools[p];
      while (cursors[p] < pool.length && seen.has(keyOf(pool[cursors[p]]))) cursors[p] += 1;
      if (cursors[p] >= pool.length) continue;
      exhausted = false;
      const calendar = pool[cursors[p]];
      cursors[p] += 1;
      seen.add(keyOf(calendar));
      out.push(calendar);
    }
  }
  return out;
}

/**
 * Normalizes a discovery-by-tags calendar (`_id` / `isStanzaPlusCalendar`) into
 * the flat shape the sections API returns, so one tile renders both sources.
 *
 * @param {object} calendar
 * @returns {object}
 */
export function toTileCalendar(calendar) {
  return {
    calendarId: calendar.calendarId ?? calendar._id,
    name: calendar.name,
    // School-first name for NCAA "<Sport> - <School>" calendars (server-computed).
    displayName: calendar.displayName,
    handle: calendar.handle,
    stanzaLink: calendar.stanzaLink,
    description: calendar.description,
    subscribers: calendar.subscribers ?? 0,
    picture: calendar.picture,
    isStanzaPlus: Boolean(calendar.isStanzaPlus ?? calendar.isStanzaPlusCalendar),
    mostRecentEvent: calendar.mostRecentEvent ?? null,
    trending: calendar.trending ?? null,
    createdAt: calendar.createdAt ?? null,
  };
}

/**
 * Maps a tile calendar to the Calendar-document shape `SubscribeButton` reads.
 *
 * @param {object} calendar - Tile calendar (sections / `toTileCalendar` shape).
 * @returns {{ _id: string, handle: string, name: string, picture: string, stanzaLink: string }}
 */
export function toSubscribeCalendar(calendar) {
  return {
    _id: calendar.calendarId,
    handle: calendar.handle,
    name: calendar.name,
    picture: calendar.picture,
    stanzaLink: calendar.stanzaLink,
  };
}
